import React, { useMemo, useRef } from 'react';
import cn from 'classnames';

import './TimeZonePicker.scss';
import { SearchDropdown } from 'components';
import { AsyncStatus } from 'enums';
import { FETextSearcher, ISearcherOnSearch, TimeZoneService } from 'services';
import { DateTime } from 'luxon';

interface ITimeZonePickerProps {
    className?: string,
    disabled?: boolean,
    hideClearButton?: boolean,
    inputRef?: React.MutableRefObject<HTMLInputElement>,
    onChange: (timeZone: string) => void,
    style?: React.CSSProperties,
    value: string, // Expects a timezone string like "US/Eastern"
}

interface ITimeZoneItem {
    zone: string,
    labelZone: string,
    labelOffset: string,
    fullText: string,
}

interface ITimeZonePickerState {
    loadError?: string,
    loadPromise?: Promise<string[]>,
    loadStatus: AsyncStatus,
    zones: string[],
}

const now = new Date();
function newTimeZoneItem(zone: string): ITimeZoneItem {
    let labelZone = '';
    let labelOffset = '';

    if (zone) {
        try {
            const nowWithZone = DateTime.now().setZone(zone)
            const nowMS = nowWithZone.valueOf();
            const zoneName = nowWithZone.zone.offsetName(nowMS, {format: 'short'});
            labelZone = `${zone} (${zoneName})`;
            labelOffset = nowWithZone.zone.formatOffset(nowMS, 'short');
        } catch (err) {
            console.warn('Invalid timezone: ', zone);
            labelZone = zone + '';
        }
    }
    
    return {
        zone: zone,
        labelZone: labelZone,
        labelOffset: labelOffset,
        fullText: labelZone + ' ' + labelOffset,
    }
}

// Allows the user to select a timezone. It's a normal search dropdown, but wired up to a list of timezone options.
// Even though each timezone item is an object, we're only outputting the timezone string as our onChange value.
export function TimeZonePicker({
    className, disabled, hideClearButton, inputRef, onChange, style, value
}: ITimeZonePickerProps) {
    const loadPromiseRef = useRef<Promise<ITimeZoneItem[]>>();
    const feSearcherRef = useRef<FETextSearcher<ITimeZoneItem>>();

    // Since "value" is a string, we need to convert it to a TimeZoneItem
    const valueItem: ITimeZoneItem = useMemo(() => {
        return newTimeZoneItem(value);
    }, [value]);

    function load(): Promise<ITimeZoneItem[]> {
        if (!loadPromiseRef.current) {
            loadPromiseRef.current = TimeZoneService.getServerTimeZones()
                .then((zones) => {
                    const now = new Date();
                    const formattedZones = zones.map(newTimeZoneItem);

                    // Initialize a frontend searcher. We're searching the labels because that's what the user can see.
                    feSearcherRef.current = new FETextSearcher(formattedZones, (item) => item.fullText);

                    return formattedZones;
                });
        }
        return loadPromiseRef.current;
    }

    const onSearch: ISearcherOnSearch<ITimeZoneItem> = function onSearch(query: string, offset: number) {
        return load().then(() => {
            return {
                results: feSearcherRef.current.search(query),
                canLoadMore: false,
            };
        });
    }

    function onChangeTimeZone(newTimeZoneItem: ITimeZoneItem) {
        // Note: "newTimeZoneItem" will be undefined if the user cleared the dropdown.
        onChange(newTimeZoneItem?.zone);
    }

    return (
        <SearchDropdown<ITimeZoneItem>
            className={cn('timeZonePicker', className)}
            disabled={disabled}
            dontDebounce={true}
            hideClearButton={hideClearButton}
            inputRef={inputRef}
            getOptionValue={opt => opt.zone}
            onChange={onChangeTimeZone}
            onSearch={onSearch}
            renderOption={opt => (
                <div className="timeZonePicker_item">
                    <div className="timeZonePicker_zoneLabel ellipsis" title={opt.labelZone}>
                        {opt.labelZone}
                    </div>
                    <div className="timeZonePicker_offsetLabel">
                        {opt.labelOffset}
                    </div>
                </div>
            )}
            style={style}
            value={valueItem}
        />
    );
};